import React, { useEffect, useState } from 'react'
import {
	Box,
	Button,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from '@mui/material'
import { AppButtonIcon, AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminCardSx, adminFieldsSx, rowDeleteButtonSx, rowIconButtonSx, rowToneSx } from '../_shared/ui'

/**
 * Module 4 — Extras Buckets admin.
 *
 * Two sections:
 *   1. Buckets — rename / reorder / add / delete.
 *   2. Service Type mappings — assign every service_type_id seen in
 *      bookings to a bucket (dropdown), or leave 'Unassigned'. An
 *      Auto-map button applies keyword-based defaults.
 *
 * Unmapped service types are shown at the top of the mapping table so
 * they get classified first.
 */
const API = '/api/my-business/admin'

export default function ExtrasBucketsPage({ embedded = false }) {
	const [buckets, setBuckets] = useState([])
	const [types,   setTypes]   = useState([])
	const [loading, setLoading] = useState(true)
	const [error,   setError]   = useState(null)
	const [saving,  setSaving]  = useState(false)

	// Bucket add form
	const [newCode, setNewCode] = useState('')
	const [newName, setNewName] = useState('')

	const reload = () => {
		setLoading(true); setError(null)
		Promise.all([
			fetch(`${API}/extras-buckets`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
			fetch(`${API}/service-bucket-mappings`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
		]).then(([b, t]) => { setBuckets(b); setTypes(t) })
		  .catch(e => setError(e.message))
		  .finally(() => setLoading(false))
	}

	useEffect(() => { reload() }, [])

	const patchBucket = (id, patch) => {
		setSaving(true)
		fetch(`${API}/extras-buckets/${id}`, {
			method: 'PATCH', headers: { 'Content-Type': 'application/json' },
			credentials: 'include', body: JSON.stringify(patch),
		}).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
		  .then(reload)
		  .catch(e => setError(e.message))
		  .finally(() => setSaving(false))
	}

	const createBucket = () => {
		const code = newCode.trim(); const name = newName.trim()
		if (!code || !name) return
		setSaving(true)
		fetch(`${API}/extras-buckets`, {
			method: 'POST', headers: { 'Content-Type': 'application/json' },
			credentials: 'include', body: JSON.stringify({ code, name }),
		}).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
		  .then(() => { setNewCode(''); setNewName(''); reload() })
		  .catch(e => setError(e.message))
		  .finally(() => setSaving(false))
	}

	const deleteBucket = (id) => {
		if (!window.confirm('Delete this bucket? Any mapping must be cleared first.')) return
		setSaving(true)
		fetch(`${API}/extras-buckets/${id}`, {
			method: 'DELETE', credentials: 'include',
		}).then(r => r.json().then(body => ({ ok: r.ok, body })))
		  .then(({ ok, body }) => {
			  if (!ok) throw new Error(body?.error || `HTTP ${body?.status || 'error'}`)
			  reload()
		  })
		  .catch(e => setError(e.message))
		  .finally(() => setSaving(false))
	}

	const setMapping = (service_type_id, bucket_id) => {
		setSaving(true)
		fetch(`${API}/service-bucket-mappings/${service_type_id}`, {
			method: 'PUT', headers: { 'Content-Type': 'application/json' },
			credentials: 'include', body: JSON.stringify({ bucket_id: bucket_id || null }),
		}).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
		  .then(reload)
		  .catch(e => setError(e.message))
		  .finally(() => setSaving(false))
	}

	const automap = (overwrite = false) => {
		setSaving(true)
		fetch(`${API}/service-bucket-mappings/automap`, {
			method: 'POST', headers: { 'Content-Type': 'application/json' },
			credentials: 'include', body: JSON.stringify({ overwrite }),
		}).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
		  .then(reload)
		  .catch(e => setError(e.message))
		  .finally(() => setSaving(false))
	}

	// Show unassigned first
	const orderedTypes = [...types].sort((a, b) => {
		if (!a.bucket_id && b.bucket_id) return -1
		if (a.bucket_id && !b.bucket_id) return  1
		return (b.n_items || 0) - (a.n_items || 0)
	})
	const unmappedCount = types.filter(t => !t.bucket_id).length

	const body = (
		<Box>
			<AdminProgress active={saving}/>
			{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}
			{loading && !buckets.length ? <AppResultsSkeleton columns={4}/> : <>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx }}>
					<AppCardTitle icon="layer-group" title="Buckets"/>
					<TableContainer sx={{ mt: 1.5 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell width={100}>Order</TableCell>
									<TableCell width={200}>Code</TableCell>
									<TableCell>Name</TableCell>
									<TableCell width={80}></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{buckets.map(b => (
									<TableRow key={b.id}>
										<TableCell>
											<TextField type="number" size="small" defaultValue={b.sort_order}
											           onBlur={(e) => {
												           const v = Number(e.target.value)
												           if (v !== b.sort_order) patchBucket(b.id, { sort_order: v })
											           }}
											           inputProps={{ style: { width: 60 } }}/>
										</TableCell>
										<TableCell><code>{b.code}</code></TableCell>
										<TableCell>
											<TextField size="small" fullWidth defaultValue={b.name}
											           onBlur={(e) => {
												           const v = e.target.value.trim()
												           if (v && v !== b.name) patchBucket(b.id, { name: v })
											           }}/>
										</TableCell>
										<TableCell>
											<AppButtonIcon icon="trash" size="small" sx={rowDeleteButtonSx}
											               onClick={() => deleteBucket(b.id)}/>
										</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell/>
									<TableCell>
										<TextField size="small" placeholder="code" fullWidth
										           value={newCode}
										           onChange={(e) => setNewCode(e.target.value)}/>
									</TableCell>
									<TableCell>
										<TextField size="small" placeholder="Display name" fullWidth
										           value={newName}
										           onChange={(e) => setNewName(e.target.value)}/>
									</TableCell>
									<TableCell>
										<AppButtonIcon icon="plus" size="small" sx={rowIconButtonSx}
										               onClick={createBucket}
										               disabled={!newCode.trim() || !newName.trim()}/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx, mb: 0 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between"
					       sx={{ mb: 2 }}>
						<AppCardTitle icon="tags" title={`Service Type mappings · ${unmappedCount} unassigned`}/>
						<Stack direction="row" gap={1}>
							<Button size="small" variant="outlined" onClick={() => automap(false)}>
								Auto-map unassigned
							</Button>
							<Button size="small" variant="text" onClick={() => automap(true)}>
								Re-apply defaults
							</Button>
						</Stack>
					</Stack>
					<TableContainer sx={{ maxHeight: 500 }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Service Type</TableCell>
									<TableCell align="right" width={90}>Items</TableCell>
									<TableCell width={260}>Bucket</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{orderedTypes.map(t => (
									<TableRow key={t.service_type_id}
									          sx={rowToneSx({ unassigned: !t.bucket_id })}>
										<TableCell>{t.service_type || <em>type#{t.service_type_id}</em>}</TableCell>
										<TableCell align="right">{new Intl.NumberFormat('es-ES').format(t.n_items)}</TableCell>
										<TableCell>
											<Select size="small" fullWidth
											        value={t.bucket_id || ''}
											        onChange={(e) => setMapping(t.service_type_id, e.target.value || null)}
											        displayEmpty>
												<MenuItem value=""><em>Unassigned</em></MenuItem>
												{buckets.map(b => (
													<MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
												))}
											</Select>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</>}
		</Box>
	)

	if (embedded) return body
	return (
		<MbPage title="Extras Buckets" subtitle="Group booking items into P&L buckets">
			{body}
		</MbPage>
	)
}
